import { useState, useMemo, useEffect } from "react";
import {
  usePaymentQueries,
  usePaymentDetail,
  useVerificationQueue,
} from "@/hooks/usePaymentQueries";
import { usePaymentVerificationStore } from "@/stores/paymentVerificationStore";
import { proofsOfDetail } from "@/lib/paymentProof";
import { PaymentVerificationQueue } from "@/components/payments/PaymentVerificationQueue";
import { PaymentDetailCard } from "@/components/payments/PaymentDetailCard";
import { PageHeader } from "@/components/common/page-header";
import { PaymentMethodSummary } from "@/components/payments/PaymentMethodSummary";
import { VerificationModal } from "@/components/payments/VerificationModal";
import { RejectionModal } from "@/components/payments/RejectionModal";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { usePermissions } from "@/hooks/usePermissions";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";
import type { PaymentVerificationDto } from "@/services/admin-payment.service";

export function PaymentVerificationPage() {
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);

  // The queue itself is readable by every admin role; settling a payment is not.
  const { hasRole } = usePermissions();
  const canSettle = hasRole(["SuperAdmin", "BillingAdmin"]);

  const { verifyPayment, rejectPayment } = usePaymentQueries();

  // Every loaded page lives in one infinite query. A refresh — the invalidation every verify or
  // reject runs — re-reads all of them in order, and "Load More" fetches the page after the last
  // one that loaded, so a failed page is asked for again and a refresh keeps the pages appended
  // after the first.
  const verificationQueue = useVerificationQueue();

  const {
    selectedPaymentId,
    setSelectedPaymentId,
    verificationModalOpen,
    openVerificationModal,
    closeVerificationModal,
    rejectionModalOpen,
    openRejectionModal,
    closeRejectionModal,
  } = usePaymentVerificationStore();

  const paymentDetail = usePaymentDetail(selectedPaymentId || "");

  // First occurrence wins: a row can shift onto the next page between two fetches when rows ahead
  // of it leave the queue, and it must not be listed twice.
  const allPayments = useMemo(() => {
    const seen = new Set<string>();
    const rows: PaymentVerificationDto[] = [];
    for (const page of verificationQueue.data?.pages ?? []) {
      for (const payment of page.data) {
        if (!seen.has(payment.paymentId)) {
          seen.add(payment.paymentId);
          rows.push(payment);
        }
      }
    }
    return rows;
  }, [verificationQueue.data]);

  // Read off the most recent page, the same one `hasNextPage` is derived from: a payment that
  // arrived after page 1 loaded is only counted there, and a total taken from page 1 would hide
  // "Load More" while a next page still exists.
  const pages = verificationQueue.data?.pages;
  const totalCount = pages?.[pages.length - 1]?.pagination?.totalItems;

  // Filter payments by selected method
  const filteredPayments = useMemo(() => {
    if (!selectedMethod) {
      return allPayments;
    }
    return allPayments.filter((p) => p.paymentMethod === selectedMethod);
  }, [allPayments, selectedMethod]);

  // The queue row the dialog is about — read from what is on screen, with no fallback: the
  // dialog is only rendered when this is found, and the auto-select effect below keeps the
  // selection inside the current page. A selection that is not in the list is one the operator
  // cannot see, and confirming an amount against a row nobody is looking at is the failure the
  // amount field exists to prevent.
  const selectedPayment =
    filteredPayments.find((p) => p.paymentId === selectedPaymentId) ?? null;

  const [modalPayment, setModalPayment] =
    useState<PaymentVerificationDto | null>(null);
  // Same snapshot discipline as `modalPayment` below, but only the id is needed: RejectionModal
  // shows no payment-specific detail (amount, notes, proof), so only the id sent to the API can
  // ever be wrong.
  const [rejectModalPaymentId, setRejectModalPaymentId] = useState<
    string | null
  >(null);

  // Snapshots `selectedPayment` the moment the modal opens (guarded by `!modalPayment`, so this
  // only fires on a FRESH open) and holds onto it for as long as the modal is open or the
  // request it started is still running. Deliberately does NOT re-run just because
  // `selectedPayment` changes while already open — the queue's own `["payments"]` invalidation
  // (fired from the mutation's onSettled) can refetch mid-request and move the selection to a
  // different row (the verified payment leaving "awaiting verification"), and an operator can
  // also click a different queue row while the dialog is still up. Either would otherwise
  // silently re-point this dialog — amount, notes and all — at a payment nobody opened it for.
  // Cleared once the modal has actually closed AND nothing is still in flight, so the next open
  // always starts from a fresh snapshot.
  useEffect(() => {
    if (verificationModalOpen && !modalPayment && selectedPayment) {
      setModalPayment(selectedPayment);
    } else if (!verificationModalOpen && !verifyPayment.isPending) {
      setModalPayment(null);
    }
  }, [
    verificationModalOpen,
    selectedPayment,
    verifyPayment.isPending,
    modalPayment,
  ]);

  useEffect(() => {
    if (rejectionModalOpen && !rejectModalPaymentId && selectedPaymentId) {
      setRejectModalPaymentId(selectedPaymentId);
    } else if (!rejectionModalOpen && !rejectPayment.isPending) {
      setRejectModalPaymentId(null);
    }
  }, [
    rejectionModalOpen,
    selectedPaymentId,
    rejectPayment.isPending,
    rejectModalPaymentId,
  ]);

  // If a verify/reject failed and the queue refreshed without the frozen payment in it — someone
  // else settled it while the operator was looking — the modal must not offer to submit again
  // against a row that no longer means what it shows. A successful mutation already closes its
  // own modal from `handleVerify`/`handleReject`'s `onSuccess`, so these only ever fire on the
  // error path (the mutation stays `isPending` — and the modal open — until its own refetch has
  // landed, see `usePaymentQueries`).
  //
  // Toasted under the SAME id the mutation's own `onError` already used (`verify-`/`reject-` +
  // paymentId): that call already told the operator the request failed with whatever the server
  // said, and this fires strictly after it settles. Sharing the id makes sonner replace that
  // toast with this sentence instead of stacking a second, more specific one underneath it —
  // when this branch does not fire (the row is still in the list), the mutation's own toast is
  // left standing as the default.
  useEffect(() => {
    if (
      verificationModalOpen &&
      modalPayment &&
      !verifyPayment.isPending &&
      !filteredPayments.some((p) => p.paymentId === modalPayment.paymentId)
    ) {
      closeVerificationModal();
      toast.error("This payment is no longer awaiting verification", {
        id: `verify-${modalPayment.paymentId}`,
      });
    }
  }, [
    verificationModalOpen,
    modalPayment,
    verifyPayment.isPending,
    filteredPayments,
    closeVerificationModal,
  ]);

  useEffect(() => {
    if (
      rejectionModalOpen &&
      rejectModalPaymentId &&
      !rejectPayment.isPending &&
      !filteredPayments.some((p) => p.paymentId === rejectModalPaymentId)
    ) {
      closeRejectionModal();
      toast.error("This payment is no longer awaiting verification", {
        id: `reject-${rejectModalPaymentId}`,
      });
    }
  }, [
    rejectionModalOpen,
    rejectModalPaymentId,
    rejectPayment.isPending,
    filteredPayments,
    closeRejectionModal,
  ]);

  const handleVerify = (notes: string | undefined, receivedAmount: number) => {
    // Never the live `selectedPaymentId`: the frozen snapshot is what the dialog is showing the
    // operator, and the two can point at different rows once the queue has refetched under it
    // (see the snapshot effect above). The modal only renders while `modalPayment` is set, so
    // this guard cannot actually turn the Confirm button into a dead end.
    if (!modalPayment) return;
    verifyPayment.mutate(
      { paymentId: modalPayment.paymentId, notes, receivedAmount },
      {
        onSuccess: () => {
          closeVerificationModal();
          setSelectedPaymentId(null);
        },
      },
    );
  };

  const handleReject = (reason: string) => {
    if (!rejectModalPaymentId) return;
    rejectPayment.mutate(
      { paymentId: rejectModalPaymentId, reason },
      {
        onSuccess: () => {
          closeRejectionModal();
          setSelectedPaymentId(null);
        },
      },
    );
  };

  // Auto-select the first payment item when queue loads or selection is invalid. Sits out
  // entirely while either modal is open or either mutation is still settling: changing the
  // selection out from under an open dialog is exactly what the snapshot effects above exist to
  // survive, and there is no reason to fight them by moving `selectedPaymentId` at the same time.
  useEffect(() => {
    if (
      verificationModalOpen ||
      rejectionModalOpen ||
      verifyPayment.isPending ||
      rejectPayment.isPending
    ) {
      return;
    }

    if (filteredPayments.length > 0) {
      const isSelectedValid = filteredPayments.some(
        (p) => p.paymentId === selectedPaymentId,
      );
      if (!selectedPaymentId || !isSelectedValid) {
        setSelectedPaymentId(filteredPayments[0].paymentId);
      }
    } else if (selectedPaymentId) {
      setSelectedPaymentId(null);
    }
  }, [
    filteredPayments,
    selectedPaymentId,
    setSelectedPaymentId,
    verificationModalOpen,
    rejectionModalOpen,
    verifyPayment.isPending,
    rejectPayment.isPending,
  ]);

  if (verificationQueue.isLoading) {
    return <LoadingSpinner />;
  }

  // A failed load must not read as "the queue is empty" — that told the operator there was
  // nothing to do when the truth is the request never answered. A failed "Load More" leaves the
  // loaded pages in `data`, so it never reaches this branch; the queue reports it inline.
  if (verificationQueue.error && !verificationQueue.data) {
    return (
      <ErrorFallback
        error={verificationQueue.error as Error}
        onRetry={() => verificationQueue.refetch()}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="shrink-0">
          <PageHeader
            title="Payment Verification"
            subtitle="Review and verify pending payments"
          />
        </div>

        {/* Method Summary Cards */}
        {verificationQueue.data && (
          <div className="shrink-0 mt-4">
            <PaymentMethodSummary
              payments={allPayments}
              totalCount={totalCount}
              selectedMethod={selectedMethod}
              onMethodClick={setSelectedMethod}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 min-h-0 flex-1">
          {/* Left: Payment Queue */}
          <PaymentVerificationQueue
            payments={filteredPayments}
            selectedPaymentId={selectedPaymentId}
            onSelectPayment={setSelectedPaymentId}
            totalCount={totalCount}
            onLoadMore={
              verificationQueue.hasNextPage
                ? // After a failure this asks for the same page again: the next page is derived
                  // from the last one that loaded.
                  () => void verificationQueue.fetchNextPage({ cancelRefetch: false })
                : undefined
            }
            // Busy during a refresh as well as during the next page. With `cancelRefetch: false` a
            // click while the loaded pages are being re-read joins that refresh and loads nothing,
            // so the button waits with it instead of looking clickable.
            isLoadingMore={verificationQueue.isFetching}
            loadMoreFailed={verificationQueue.isFetchNextPageError}
          />

          {/* Right: Payment Detail */}
          <div className="overflow-y-auto">
            {!selectedPaymentId && (
              <Alert className="py-3">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  Select a payment from the queue to view details
                </AlertDescription>
              </Alert>
            )}

            {selectedPaymentId && paymentDetail.isLoading && <LoadingSpinner />}

            {selectedPaymentId && paymentDetail.error && !paymentDetail.data && (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  Could not load this payment's details.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => paymentDetail.refetch()}
                  disabled={paymentDetail.isFetching}
                >
                  Retry
                </Button>
              </div>
            )}

            {selectedPaymentId && paymentDetail.data && (
              <PaymentDetailCard
                payment={paymentDetail.data}
                {...(canSettle
                  ? {
                      onVerify: openVerificationModal,
                      onReject: openRejectionModal,
                    }
                  : {})}
                isVerifying={verifyPayment.isPending}
                isRejecting={rejectPayment.isPending}
              />
            )}
          </div>
        </div>
      </div>

      {modalPayment && (
        <VerificationModal
          open={verificationModalOpen}
          onOpenChange={closeVerificationModal}
          payment={{
            paymentOrderNumber: modalPayment.paymentOrderNumber,
            amount: modalPayment.amount,
            currency: modalPayment.currency,
            qrProvider: modalPayment.qrProvider,
            proofs: proofsOfDetail(modalPayment),
          }}
          onConfirm={handleVerify}
          isLoading={verifyPayment.isPending}
        />
      )}

      {rejectModalPaymentId && (
        <RejectionModal
          open={rejectionModalOpen}
          onOpenChange={closeRejectionModal}
          onConfirm={handleReject}
          isLoading={rejectPayment.isPending}
        />
      )}
    </>
  );
}
